import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { FORMA_PAGAMENTO_ROTULO, formatarDataHora, formatarReais, type FormaPagamento } from "@caixaflow/shared";
import { CHAVE_CAIXA_ATUAL, useCaixaAtual } from "../../api/caixa";
import { api } from "../../api/cliente";
import { useSessao } from "../../api/sessao";
import type { CaixaDetalhe } from "../../api/tipos";
import { Diferenca, IndicadoresCaixa } from "../../components/Caixa";
import { Campo, EntradaReais } from "../../components/Campos";
import { Carregando, FalhaAoCarregar, mensagemDeErro } from "../../components/Estados";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { useFila } from "../../offline/useFila";
import { lembrar } from "../../offline/ultimoConhecido";

type Contado = Record<FormaPagamento, number | null>;
const contadoVazio: Contado = { DINHEIRO: null, PIX: null, DEBITO: null, CREDITO: null };

export function PaginaFecharCaixa() {
  const { data: sessao } = useSessao();
  const atual = useCaixaAtual();
  // Sempre busca de novo ao entrar: o esperado precisa refletir todas as vendas.
  useEffect(() => {
    atual.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (atual.isPending || !sessao) return <Carregando />;
  if (atual.error && atual.data === undefined) return <FalhaAoCarregar erro={atual.error} tentarDeNovo={() => atual.refetch()} />;
  // Sem redirecionar automaticamente: logo após fechar, o "caixa atual" vira null enquanto a
  // tela navega para o comprovante — um <Navigate> aqui atropelaria essa navegação.
  if (!atual.data) {
    return (
      <div className="card vazio">
        Nenhum caixa aberto. <Link to="/caixa">Ir para o Caixa</Link>
      </div>
    );
  }
  return <FormFechamento caixa={atual.data} usuarioId={sessao.usuario.id} />;
}

function FormFechamento({ caixa, usuarioId }: { caixa: CaixaDetalhe; usuarioId: string }) {
  const [contado, setContado] = useState<Contado>(contadoVazio);
  const [observacoes, setObservacoes] = useState("");
  const [confirmando, setConfirmando] = useState(false);
  const [erroLocal, setErroLocal] = useState<string | null>(null);
  const fila = useFila(usuarioId);
  const filaDoCaixa = fila.itens.filter((v) => v.caixaId === caixa.id);
  const navegar = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const fechar = useMutation({
    mutationFn: () =>
      api<CaixaDetalhe>(`/caixas/${caixa.id}/fechar`, "POST", { contado, observacoes: observacoes.trim() || undefined }),
    onSuccess: (fechado) => {
      qc.setQueryData(CHAVE_CAIXA_ATUAL, null);
      lembrar("caixa-atual", null);
      qc.setQueryData(["caixa", fechado.id], fechado);
      qc.invalidateQueries({ queryKey: ["caixas"] });
      toast(`Caixa #${fechado.numero} fechado`);
      navegar(`/caixas/${fechado.id}`, { replace: true });
    },
    onError: () => setConfirmando(false),
  });

  const linhas = caixa.resumo.porForma.map((l) => ({
    ...l,
    contado: contado[l.formaPagamento],
    diferenca: contado[l.formaPagamento] === null ? null : contado[l.formaPagamento]! - l.esperadoCentavos,
  }));
  const esperadoTotal = linhas.reduce((s, l) => s + l.esperadoCentavos, 0);
  const tudoInformado = linhas.every((l) => l.contado !== null);
  const contadoTotal = linhas.reduce((s, l) => s + (l.contado ?? 0), 0);

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    if (!tudoInformado) {
      setErroLocal("Informe o valor contado de todas as formas (use 0,00 quando não houver).");
      return;
    }
    setErroLocal(null);
    setConfirmando(true);
  };

  if (filaDoCaixa.length > 0) {
    return (
      <div className="pilha">
        <div className="aviso erro" role="alert">
          Há {filaDoCaixa.length} venda(s) deste caixa guardada(s) no aparelho, ainda não registrada(s) no servidor. Envie ou
          resolva essas vendas antes de fechar o caixa — senão o fechamento sairia sem elas.
        </div>
        <Link className="btn primario" to="/caixa">
          Voltar ao caixa
        </Link>
      </div>
    );
  }

  return (
    <form className="pilha" onSubmit={enviar}>
      <div className="linha">
        <span className="mudo">
          <b style={{ color: "var(--ink)" }}>Caixa #{caixa.numero}</b> · {caixa.evento.nome} · aberto em{" "}
          {formatarDataHora(caixa.abertoEm)}
        </span>
        <Link to="/caixa" className="btn pequeno fantasma">
          Voltar ao caixa
        </Link>
      </div>

      <IndicadoresCaixa totais={caixa.resumo.totais} />

      <section>
        <h2>Conferência</h2>
        <p className="mudo pequeno" style={{ marginTop: 0 }}>
          Conte o dinheiro da gaveta e confira os relatórios da maquininha e do Pix. O esperado em dinheiro já inclui o fundo
          de troco de {formatarReais(caixa.fundoTrocoCentavos)}.
        </p>
        {erroLocal && (
          <div className="aviso erro" role="alert" style={{ marginBottom: 10 }}>
            {erroLocal}
          </div>
        )}
        {/* Um cartão por forma: cabe no celular sem rolagem lateral. */}
        <div className="conferencia">
          {linhas.map((l) => (
            <div key={l.formaPagamento} className="card pad conf-linha">
              <div className="linha">
                <b>{FORMA_PAGAMENTO_ROTULO[l.formaPagamento]}</b>
                <span className="mudo pequeno">
                  {l.quantidadeVendas} {l.quantidadeVendas === 1 ? "venda" : "vendas"}
                </span>
              </div>
              <div className="conf-grade">
                <div>
                  <span className="rotulo">Esperado</span>
                  <b className="num">{formatarReais(l.esperadoCentavos)}</b>
                </div>
                <label className="campo" htmlFor={`contado-${l.formaPagamento}`}>
                  <span className="rotulo">Contado</span>
                  <EntradaReais
                    id={`contado-${l.formaPagamento}`}
                    centavos={l.contado}
                    onChange={(c) => setContado((atual) => ({ ...atual, [l.formaPagamento]: c }))}
                  />
                </label>
                <div>
                  <span className="rotulo">Diferença</span>
                  {l.diferenca === null ? <span className="mudo">—</span> : <Diferenca centavos={l.diferenca} />}
                </div>
              </div>
              {l.formaPagamento !== "DINHEIRO" && l.contado === null && (
                <button
                  type="button"
                  className="link-botao"
                  style={{ alignSelf: "flex-start", padding: 0 }}
                  onClick={() => setContado((c) => ({ ...c, [l.formaPagamento]: l.esperadoCentavos }))}
                >
                  = esperado
                </button>
              )}
            </div>
          ))}
          <div className="card pad conf-grade conf-total">
            <div>
              <span className="rotulo">Total esperado</span>
              <b className="num">{formatarReais(esperadoTotal)}</b>
            </div>
            <div>
              <span className="rotulo">Total contado</span>
              <b className="num">{tudoInformado ? formatarReais(contadoTotal) : "—"}</b>
            </div>
            <div>
              <span className="rotulo">Diferença</span>
              {tudoInformado ? <Diferenca centavos={contadoTotal - esperadoTotal} /> : <span className="mudo">—</span>}
            </div>
          </div>
        </div>
      </section>

      <Campo rotulo="Observações de fechamento" dica="opcional">
        <textarea value={observacoes} onChange={(e) => setObservacoes(e.target.value)} maxLength={500} />
      </Campo>
      {fechar.error && (
        <div className="aviso erro" role="alert">
          {mensagemDeErro(fechar.error)}
        </div>
      )}
      <button className="btn primario largo" type="submit">
        Fechar caixa
      </button>

      <Modal aberto={confirmando} titulo="Fechar caixa?" onFechar={() => setConfirmando(false)}>
        <div className="form">
          <p style={{ margin: 0 }}>
            Total contado {formatarReais(contadoTotal)} para {formatarReais(esperadoTotal)} esperado:{" "}
            <Diferenca centavos={contadoTotal - esperadoTotal} />.
          </p>
          <p className="mudo pequeno" style={{ margin: 0 }}>
            Depois de fechado, o caixa não aceita vendas nem cancelamentos. Só um ADMIN pode reabrir, com motivo.
          </p>
          <div className="modal-acoes">
            <button type="button" className="btn fantasma" onClick={() => setConfirmando(false)}>
              Revisar
            </button>
            <button type="button" className="btn primario" disabled={fechar.isPending} onClick={() => fechar.mutate()}>
              {fechar.isPending ? "Fechando…" : "Confirmar fechamento"}
            </button>
          </div>
        </div>
      </Modal>
    </form>
  );
}
